import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { INVOICE_FEES_TYPES, INVOICE_SECTIONS } from "@/constants";
import { fCurrencyDecimal, toNonNegativeNumber } from "@/utils/formatNumber";
import { IInvoiceFeesItem } from "@/store/slices/types/invoiceSliceTypes";
import { FeesItemsSectionProps } from "@/utils/interface";
import SectionCard from "./SectionCard";

const FEES_OPTIONS = Object.values(INVOICE_FEES_TYPES);

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const FeesItemsSection = ({
  disabled,
  items,
  loading,
  expanded,
  isDraftInvoice,
  onExpandedChange,
  onChange,
}: FeesItemsSectionProps) => {
  const addItem = (feeName: string) => {
    if (disabled) return;
    onChange([...items, { id: newId(), feeName, description: "", qty: 1, unitPriceDollars: 0 }]);
  };

  const updateItem = (id: string, patch: Partial<IInvoiceFeesItem>) => {
    if (disabled) return;
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id: string) => {
    if (disabled) return;
    onChange(items.filter((it) => it.id !== id));
  };

  return (
    <SectionCard
      title={INVOICE_SECTIONS.feesItems}
      subtitle="Mix licence fees, referral fees and custom charges (Recommanded: Use service-usages subtotal) "
      disabled={disabled}
      collapsible
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <Stack direction="column" rowGap={2}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            padding: "8px 12px",
            backgroundColor: "neutral.50",
            borderRadius: "sm",
          }}
        >
          <FormLabel style={{ width: 140, margin: 0 }}>Fee</FormLabel>
          <FormLabel style={{ flex: 1, margin: 0 }}>DESCRIPTION</FormLabel>
          <FormLabel style={{ width: 70, margin: 0, textAlign: "right", justifyContent: "flex-end" }}>QTY</FormLabel>
          <FormLabel style={{ width: 100, margin: 0, textAlign: "right", justifyContent: "flex-end" }}>
            UNIT PRICE
          </FormLabel>
          <FormLabel style={{ width: 110, margin: 0, textAlign: "right", justifyContent: "flex-end" }}>
            AMOUNT
          </FormLabel>
          {isDraftInvoice && <span style={{ width: 32 }} />}
        </Stack>

        {loading && (
          <Stack alignItems="center" style={{ padding: "12px 0" }}>
            <CircularProgress size="sm" />
          </Stack>
        )}

        {!loading && items.length === 0 && (
          <Typography level="body-sm" textColor="neutral.500" style={{ textAlign: "center", padding: "8px 0" }}>
            No fees items yet. Add one below.
          </Typography>
        )}

        {!loading &&
          items.map((item) => {
            const amount = (item.qty || 0) * (item.unitPriceDollars || 0);
            return (
              <Stack key={item.id} direction="row" alignItems="center" columnGap={1}>
                <Box sx={{ width: 140 }}>
                  <Select
                    value={item.feeName as unknown as Record<string, unknown>}
                    disabled={disabled}
                    onChange={(_e, val) => val && updateItem(item.id, { feeName: val as unknown as string })}
                  >
                    {FEES_OPTIONS.map((k) => (
                      <Option key={k} value={k}>
                        {k}
                      </Option>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Input
                    placeholder="Description"
                    value={item.description}
                    disabled={disabled}
                    onChange={(e) => updateItem(item.id, { description: e.target.value })}
                  />
                </Box>
                <Box sx={{ width: 70 }}>
                  <Input
                    type="number"
                    value={item.qty}
                    disabled={disabled}
                    onChange={(e) => updateItem(item.id, { qty: toNonNegativeNumber(e.target.value) })}
                    slotProps={{ input: { min: 0, style: { textAlign: "right" } } }}
                  />
                </Box>
                <Box sx={{ width: 100 }}>
                  <Input
                    type="number"
                    value={item.unitPriceDollars}
                    disabled={disabled}
                    onChange={(e) => updateItem(item.id, { unitPriceDollars: Number(e.target.value) })}
                    slotProps={{ input: { style: { textAlign: "right" } } }}
                  />
                </Box>
                <Typography style={{ width: 110, textAlign: "right", fontWeight: 600, marginRight: "12px" }}>
                  {fCurrencyDecimal(amount)}
                </Typography>
                {isDraftInvoice && (
                  <IconButton
                    size="sm"
                    disabled={disabled}
                    onClick={() => removeItem(item.id)}
                    aria-label="Remove fee item"
                  >
                    <CloseIcon style={{ fontSize: "1rem" }} />
                  </IconButton>
                )}
              </Stack>
            );
          })}
        {isDraftInvoice && (
          <Stack direction="row" columnGap={1} style={{ marginTop: 4 }}>
            <Button
              variant="secondary"
              size="sm"
              startDecorator={<AddIcon />}
              disabled={disabled}
              onClick={() => addItem(INVOICE_FEES_TYPES.licenceFee)}
            >
              Licence fee
            </Button>
            <Button
              variant="secondary"
              size="sm"
              startDecorator={<AddIcon />}
              disabled={disabled}
              onClick={() => addItem(INVOICE_FEES_TYPES.referralFee)}
            >
              Referral fee
            </Button>
            <Button
              variant="secondary"
              size="sm"
              startDecorator={<AddIcon />}
              disabled={disabled}
              onClick={() => addItem(INVOICE_FEES_TYPES.customFee)}
            >
              Custom fee
            </Button>
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};

export default FeesItemsSection;
